import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Component, Inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { ChangeSetChangesDialogData } from '@components/change-set-changes-dialog/1.0';
import { ButtonStyle } from '@constants/button/1.0';
import { MessageType } from '@constants/message/1.0';
import { ThemePalette } from '@angular/material/core';
import { ConfirmationComponent, ConfirmationDialogData } from '@components/confirmation/1.0';
import { ChangeSetsService } from '@services/change-sets/1.0/change-sets.service';
import { Composite, CompositeType } from '@interfaces/composite/1.0';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor, KeyValuePipe } from '@angular/common';
import { ButtonComponent } from '../../button/1.0/button.component';

@Component({
  selector: 'txp-change-set-changes-dialog',
  templateUrl: './change-set-changes-dialog.component.html',
  styleUrls: ['./change-set-changes-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    NgIf,
    NgFor,
    ButtonComponent,
    MatDialogActions,
    TranslateModule,
    KeyValuePipe,
  ],
})
export class ChangeSetChangesDialogComponent {
  buttonStyle = ButtonStyle;
  disableContinueBtn: boolean;
  isLoading = false;
  messageType = MessageType;
  itemTypeMap: { [key: string]: any };

  public color: ThemePalette = 'primary';

  constructor(
    private changeSetsService: ChangeSetsService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ChangeSetChangesDialogComponent>,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: ChangeSetChangesDialogData,
  ) {
    this.itemTypeMap = data.itemList.reduce((obj, item) => {
      const isComposite = item.entityType === 'COMPOSITE';
      const compositeType =
        (item as Composite).compositeType === CompositeType.CONTAINER
          ? CompositeType.CONTAINER
          : CompositeType.COMBO;
      const typeToUse = isComposite ? compositeType : item.entityType;

      if (!obj[typeToUse]) {
        obj[typeToUse] = [];
      }

      obj[typeToUse].push(item);
      return obj;
    }, {});
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  onClearChanges() {
    const confirmObservable = this.changeSetsService.delete(this.data.changeSetId).pipe(
      catchError(() => {
        return throwError(() => this.translateService.instant('catalog.common.deleteError'));
      }),
    );
    const modalContent: ConfirmationDialogData = {
      title: this.translateService.instant(
        'catalog.changeSets.viewChangeSetChangesDialog.clearAllChanges',
      ),
      message: this.translateService.instant(
        'catalog.changeSets.viewChangeSetChangesDialog.message',
      ),
      cancelText: this.translateService.instant('form.button.cancel'),
      confirmText: this.translateService.instant('form.button.confirm'),
      confirmObservable,
    };
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      data: modalContent,
    });

    dialogRef.afterClosed().subscribe((confirmAction) => {
      if (confirmAction) {
        this.dialogRef.close(true);
      }
    });
  }

  onAction() {
    if (this.data.confirmObservable) {
      this.isLoading = true;
      this.data.confirmObservable.subscribe({
        complete: () => {
          this.isLoading = false;
          this.dialogRef.close(true);
        },
        error: (err: string) => {
          this.isLoading = false;
          this.dialogRef.close(new Error(err));
        },
      });
    }
  }
}
